// MobileLogSheet — mobile-only BottomSheet for logging a progress / budget
// amount against a single block (per `docs/spec/wishlist/DESIGN.md §10.1` +
// `loop-goals/W34.2-COMPREHENSIVE-PROMPT.md` outcome W34.2.0).
//
// The component is split across files to honour the 12KB sheet ceiling
// (AGENTS.md §3 #10):
//   - MobileLogSheet.tsx         — entry point + body wrapper
//   - MobileLogSheetKeypad.tsx   — display row + direction toggle + digit
//                                  keypad + note input
//   - MobileLogSheetLogList.tsx  — last-N log list with swipe-to-delete
//
// All API calls go through typed wrappers in `lib/api/wishlist.ts`
// (listProgressLog / addProgressLog / deleteProgressLog). Toast feedback
// uses the existing `useToast` (components/ui/toast) per AGENTS.md §3 #13.

import { useCallback, useEffect, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { BottomSheet } from "@/components/patterns/BottomSheet";
import { useToast } from "@/components/ui/toast";
import { useJsonDraft } from "@/lib/hooks/useDraft";
import { useAuth } from "@/lib/auth/AuthProvider";
import {
  addProgressLog,
  deleteProgressLog,
  listProgressLog,
  updateProgressLog,
  type ProgressLogOut,
} from "@/lib/api/wishBlocks";
import { type WishBlockOut } from "@/lib/api/wishlist";
import { queryKeys } from "@/lib/queries/keys";
import {
  DisplayRow,
  DirectionToggle,
  DigitKeypad,
  NoteField,
} from "./MobileLogSheetKeypad";
import {
  deriveDirection,
  deriveTarget,
  formatDisplay,
  isLoggable,
} from "./mobileLogSheet.helpers";
import { RecentLogs } from "./MobileLogSheetLogList";

// W35.5: the pure helpers used to be re-exported from here, which made this
// file a mixed component/function module (react-refresh
// /only-export-components). They now live in `./mobileLogSheet.helpers`;
// import them from there instead.

export interface MobileLogSheetProps {
  wishId: string;
  block: WishBlockOut | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MobileLogSheet({
  wishId,
  block,
  open,
  onOpenChange,
}: MobileLogSheetProps) {
  if (!block) {
    // Inert placeholder sheet — keeps Radix mounted for a clean close
    // animation (matches MobileActionSheet pattern).
    return (
      <BottomSheet
        open={open}
        onOpenChange={onOpenChange}
        variant="mobile"
      >
        <div />
      </BottomSheet>
    );
  }
  if (!isLoggable(block)) {
    // Non-loggable block type — close immediately (defensive: callers
    // shouldn't pass notes/checklist/link/photo, but guard anyway).
    return (
      <BottomSheet
        open={open}
        onOpenChange={onOpenChange}
        variant="mobile"
      >
        <div />
      </BottomSheet>
    );
  }
  return (
    <LogSheetBody
      wishId={wishId}
      block={block}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
}

function LogSheetBody({
  wishId,
  block,
  open,
  onOpenChange,
}: MobileLogSheetProps & { block: WishBlockOut }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();

  const direction = deriveDirection(block);
  const target = deriveTarget(block);

  // W56.0 I — the draft. A session redirect in the middle of an entry used to
  // lose the amount and the note, and the keypad's digits are the slowest
  // thing in the app to type. `useJsonDraft` is the same mechanism the other
  // form sheets use, so the whole form travels as one value.
  const logDraft = useJsonDraft<{
    amountText: string;
    directionSign: "plus" | "minus";
    note: string;
  }>(`wish-log:${block.id}`, { amountText: "", directionSign: "plus", note: "" });

  const [amountText, setAmountText] = useState<string>(logDraft.value.amountText);
  const [directionSign, setDirectionSign] = useState<"plus" | "minus">(
    logDraft.value.directionSign,
  );
  const [note, setNote] = useState<string>(logDraft.value.note);
  // W50.3 D-1 — which existing entry the form is currently editing, if any.
  // `null` means "compose a new one", which is the state this sheet was born in
  // and the one every existing code path below still handles unchanged.
  const [editingLogId, setEditingLogId] = useState<string | null>(null);

  /**
   * The three change handlers, each saving the **whole** form.
   *
   * Saving the whole value (rather than one field) is the point of
   * `useJsonDraft`: a draft that kept only the last edited field would be a
   * form half-restored, which is worse than losing both — it looks like the
   * save half-worked.
   */
  const updateAmount = useCallback(
    (next: string) => {
      setAmountText(next);
      logDraft.save({ amountText: next, directionSign, note });
    },
    [directionSign, logDraft, note],
  );
  const updateSign = useCallback(
    (next: "plus" | "minus") => {
      setDirectionSign(next);
      logDraft.save({ amountText, directionSign: next, note });
    },
    [amountText, logDraft, note],
  );
  const updateNote = useCallback(
    (next: string) => {
      setNote(next);
      logDraft.save({ amountText, directionSign, note: next });
    },
    [amountText, directionSign, logDraft],
  );

  // Reset transient state when the block changes — **to the draft**, not to
  // nothing, so a redirect that unmounted the sheet does not cost the entry.
  useEffect(() => {
    setAmountText(logDraft.value.amountText);
    setDirectionSign(logDraft.value.directionSign);
    setNote(logDraft.value.note);
    setEditingLogId(null);
  }, [block.id, logDraft.value]);

  const invalidateAll = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["wishes", "detail"] });
    void queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
    void queryClient.invalidateQueries({ queryKey: ["wishes", "stats"] });
    void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
  }, [queryClient]);

  // Leave edit mode and go back to a blank form, without closing the sheet.
  const cancelEdit = useCallback(() => {
    setEditingLogId(null);
    setAmountText("");
    setNote("");
  }, []);

  const startEdit = useCallback((item: ProgressLogOut) => {
    setEditingLogId(item.id);
    // The stored amount is always positive (DATA_MODEL §1.2 puts the sign on the
    // block), so it drops straight into the keypad. The direction toggle is left
    // alone: it describes the block, not this entry, and re-deriving it here
    // would overwrite a choice the person made for this session.
    setAmountText(String(Math.abs(item.amount_delta)));
    setNote(item.note ?? "");
  }, []);

  // Auto-close on submit success — let the parent own the open state so
  // the close animation can finish before unmount.
  const addMut = useMutation({
    mutationFn: async () => {
      const delta = Number(amountText);
      if (!Number.isFinite(delta) || delta <= 0) return null;
      return addProgressLog(wishId, block.id, {
        amount_delta: Math.abs(delta),
        note: note.trim() || null,
      });
    },
    onSuccess: (data) => {
      if (!data) return;
      const formatted = formatDisplay(
        Math.abs(data.amount_delta),
        target,
        directionSign,
      );
      toast.push({
        message: `已記錄 ${formatted}`,
        variant: "you",
      });
      // Invalidate caches so the parent detail page reflects the new log
      // + the recomputed `progress_percent`.
      invalidateAll();
      setAmountText("");
      setNote("");
      // The entry is on the server now — the draft has nothing left to guard.
      logDraft.clear();
      onOpenChange(false);
    },
    onError: () => {
      toast.push({ message: "記錄失敗，請稍後再試", variant: "danger" });
    },
  });

  // W50.3 D-1 — the missing U. Same shape as `addMut` on purpose: same
  // validation, same cache invalidation, same close-on-success, so the two paths
  // cannot drift apart as they would if edit were "add, then quietly re-add".
  const updateMut = useMutation({
    mutationFn: async (logId: string) => {
      const delta = Number(amountText);
      if (!Number.isFinite(delta) || delta <= 0) return null;
      return updateProgressLog(wishId, block.id, logId, {
        amount_delta: Math.abs(delta),
        note: note.trim() || null,
      });
    },
    onSuccess: (data) => {
      if (!data) return;
      const formatted = formatDisplay(
        Math.abs(data.amount_delta),
        target,
        directionSign,
      );
      toast.push({ message: `已更新為 ${formatted}`, variant: "you" });
      invalidateAll();
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "blocks", block.id, "log"],
      });
      cancelEdit();
      logDraft.clear();
      onOpenChange(false);
    },
    onError: () => {
      toast.push({ message: "更新失敗，請稍後再試", variant: "danger" });
    },
  });

  const logQuery = useQuery({
    queryKey: ["wishes", "detail", wishId, "blocks", block.id, "log"],
    queryFn: () => listProgressLog(wishId, block.id, { limit: 5 }),
    enabled: open,
    staleTime: 30_000,
  });

  const deleteMut = useMutation({
    mutationFn: async (logId: string) =>
      deleteProgressLog(wishId, block.id, logId),
    onSuccess: () => {
      toast.push({ message: "已刪除記錄", variant: "default" });
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "blocks", block.id, "log"],
      });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "detail"] });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
    onError: () => {
      toast.push({ message: "刪除記錄失敗", variant: "danger" });
    },
  });

  const numericAmount = Number(amountText);
  const submitDisabled =
    !Number.isFinite(numericAmount) ||
    numericAmount <= 0 ||
    numericAmount > target.max ||
    addMut.isPending ||
    updateMut.isPending;

  const sign = direction === "both" ? directionSign : "plus";
  const editing = editingLogId !== null;

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      variant="mobile"
      // W56.0 I — it is a form: a stray tap on the backdrop must not throw away
      // the amount and the note.
      dismissibleOnBackdrop={false}
      title={
        editing
          ? `更新「${block.title || block.block_type}」的記錄`
          : `記錄「${block.title || block.block_type}」`
      }
      description={
        target.unit === "currency"
          ? `目標 ${formatDisplay(target.max, target, "plus")}`
          : `目標 ${target.max}%`
      }
      className="max-h-[90vh] overflow-hidden flex flex-col"
    >
      <div
        data-component="MobileLogSheet"
        data-testid="mobile-log-sheet"
        data-block-id={block.id}
        data-direction={direction}
        className="flex flex-col gap-s-3 -mx-5 -mb-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]"
      >
        <DisplayRow
          amountText={amountText}
          target={target}
          sign={sign}
        />

        {direction === "both" ? (
          <DirectionToggle
            value={directionSign}
            onChange={updateSign}
          />
        ) : null}

        <DigitKeypad
          value={amountText}
          onChange={updateAmount}
          max={target.max}
          unit={target.unit}
        />

        <NoteField note={note} onChange={updateNote} />

        <RecentLogs
          items={logQuery.data?.items ?? []}
          currentUserId={user?.id ?? null}
          onDelete={(id) => deleteMut.mutate(id)}
          onEdit={startEdit}
          editingId={editingLogId}
          pendingDeleteId={
            deleteMut.isPending ? deleteMut.variables : null
          }
        />

        <div className="flex items-center gap-s-2 px-s-5 pt-s-2">
          <button
            type="button"
            onClick={editing ? cancelEdit : () => onOpenChange(false)}
            data-testid="mobile-log-cancel"
            className="press min-h-12 flex-1 rounded-full border border-hairline bg-paper-elevated text-ink-1 text-body-sm font-semibold"
          >
            {editing ? "取消編輯" : "取消"}
          </button>
          <button
            type="button"
            onClick={() =>
              editing
                ? updateMut.mutate(editingLogId)
                : addMut.mutate()
            }
            disabled={submitDisabled}
            data-testid="mobile-log-submit"
            className="press min-h-12 flex-1 rounded-full bg-ink-1 text-paper text-body-sm font-semibold disabled:opacity-40"
          >
            {addMut.isPending || updateMut.isPending
              ? "送出中…"
              : editing
                ? "更新"
                : "記錄"}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}